'use no memo';

/* eslint-disable prefer-destructuring */
import React, { useContext, useEffect } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

import { FULFILLMENT_METHODS } from 'helpers/order';

import { SlideUpIn } from 'components/Animations/SlideUpIn';
import FadeIn from 'components/Animations/FadeIn';

import { ModalContext } from 'context/ModalContext';
import { OrderContext } from 'context/OrderContext';
import { ThemeContext } from 'context/ThemeContext';

import useIsMobile from 'hooks/useIsMobile';
import usePlatform from 'hooks/usePlatform';
import { isYelpMobile } from 'helpers/yelp';

import styles from './styles.module.scss';

function transformTo({ isMobile, isYelpPlatform, isCentered }) {
  if (isMobile) {
    return isCentered ? 'translate(-50%,-50%)' : 'translate(-50%,-20%)';
  }

  if (isYelpPlatform) {
    return 'translate(-50%,0%)';
  }

  return 'translate(-50%,-50%)';
}

function getYelpMaxHeight() {
  // We need this to restrict the modal height within the Yelp iframe.
  // Without it we don't get a scrollbar since the modal stretches within
  // the "page" height which can be 1000's of px high depending on menu size
  const iframeHeight = window.outerHeight;
  return Math.floor(iframeHeight * 0.85);
}

// eslint-disable-next-line complexity
function ModalWrapper(props) {
  const { children, background = false } = props;

  const { closeModal, isModalOpen } = useContext(ModalContext);
  const { updateOrder } = useContext(OrderContext);
  const { theme } = useContext(ThemeContext);
  const { isYelpPlatform } = usePlatform();

  const isYelpDesktop = isYelpPlatform && !isYelpMobile();
  const isMobile = useIsMobile();

  const maxHeight = isYelpDesktop ? getYelpMaxHeight() : undefined;
  const noCloseOverlay = children.props?.noCloseOverlay;

  const isAlert = children.props?.isAlert;
  const isCentered = children.props?.isCentered;
  const isInverse = children.props?.modalBackgroundInverse;

  useEffect(() => {
    if (isModalOpen && !background) {
      document.getElementById('modalWrapper').focus({ preventScroll: true });
      document.getElementById('root').setAttribute('inert', 'true');
    }
  }, [isModalOpen]);

  // Focus the foreground modal when it mounts (handles stacked case where
  // isModalOpen was already true when this modal was pushed onto the stack).
  useEffect(() => {
    if (!background) {
      document.getElementById('modalWrapper')?.focus({ preventScroll: true });
    }
  }, []);

  function handleClose() {
    if (noCloseOverlay) return;

    // fulfillment details modal:
    // to be run in the case of delivery address error when user does not want to reenter address. we should default back to pickup
    if (children.props?.shouldDefaultToPickup) {
      updateOrder({ fulfill_method: FULFILLMENT_METHODS.pickup });
    }

    if (children.props?.onClose) children.props?.onClose();

    closeModal();
  }

  return ReactDOM.createPortal(
    <div
      className={classNames(styles.modal, `theme-${theme}`, {
        [styles.alert]: isAlert,
        [styles.centered]: isCentered,
      })}
      id={background ? undefined : 'modalWrapper'}
      tabIndex={background ? undefined : -1}
      // eslint-disable-next-line react/no-unknown-property
      inert={background ? '' : undefined}
      aria-hidden={background ? true : undefined}
    >
      <SlideUpIn
        maxHeight={maxHeight}
        className={classNames(styles.modalWrapper, {
          [styles.backgroundDark]: isInverse,
        })}
        transformFrom="translate(-50%,100%)"
        transformTo={transformTo({ isYelpPlatform, isMobile, isCentered })}
      >
        <FadeIn duration={250} delay={350}>
          <div>{children}</div>
        </FadeIn>
      </SlideUpIn>
      {!background && (
        <div
          className={styles.overlay}
          onClick={handleClose}
          aria-hidden="true"
          data-testid="ModalOverlay"
        />
      )}
    </div>,
    document.querySelector('#modal-root')
  );
}

export default ModalWrapper;
